/**
 * /assets/catalog — the asset catalog (ADR-0024 Phase 4). List with the DERIVED
 * status the API returns (available / assigned / retired / lost — never inferred
 * client-side, D5), filter by category + status, create, edit, retire/mark-lost.
 */
import { createRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { listCategories, listAssets, createAsset, updateAsset, returnAsset, type Asset } from '../api/assets.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { AssetStatusBadge, TabButton } from './assetShared.js';

export const assetCatalogRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/assets/catalog',
  component: AssetCatalogPage,
});

const STATUS_FILTERS = ['all', 'available', 'assigned', 'retired', 'lost'] as const;

function AssetCatalogPage(): JSX.Element {
  const qc = useQueryClient();
  const [categoryId, setCategoryId] = useState('');
  const [statusIdx, setStatusIdx] = useState(0);
  const status = STATUS_FILTERS[statusIdx]!;

  const cats = useQuery({ queryKey: ['asset', 'categories'], queryFn: listCategories });
  const categories = cats.data?.categories ?? [];
  const assetsQ = useQuery({
    queryKey: ['asset', 'catalog', categoryId, status],
    queryFn: () => listAssets({ categoryId: categoryId || undefined, status: status === 'all' ? undefined : status }),
  });
  const assets = assetsQ.data?.assets ?? [];
  const catLabel = useMemo(() => new Map(categories.map((c) => [c.id, c.label])), [categories]);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['asset', 'catalog'] });

  return (
    <section className="p-8 space-y-6">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">Asset catalog</h1>
        <p className="text-sm text-gray-400">Every company asset and its current status.</p>
      </header>

      <CreateAssetForm categories={categories.filter((c) => c.active)} onCreated={invalidate} />

      <div className="flex flex-wrap items-center gap-3">
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-label="Filter by category" className="rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <div className="flex gap-1.5">
          {STATUS_FILTERS.map((s, i) => <TabButton key={s} active={i === statusIdx} onClick={() => setStatusIdx(i)}>{s}</TabButton>)}
        </div>
      </div>

      {assetsQ.isLoading ? <p className="text-sm text-gray-400">Loading…</p> : null}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
            <tr><th className="py-2 pr-4">Tag</th><th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Category</th><th className="py-2 pr-4">Serial</th><th className="py-2 pr-4">Status</th><th className="py-2">Actions</th></tr>
          </thead>
          <tbody>
            {assets.map((a) => <AssetRow key={a.id} a={a} categoryLabel={catLabel.get(a.categoryId) ?? '—'} onChanged={invalidate} />)}
            {assets.length === 0 && !assetsQ.isLoading ? <tr><td colSpan={6} className="py-4 text-gray-500">No assets match.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CreateAssetForm({ categories, onCreated }: { categories: { id: string; label: string }[]; onCreated: () => void }): JSX.Element {
  const [assetTag, setTag] = useState('');
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [serial, setSerial] = useState('');
  const create = useMutation({
    mutationFn: () => createAsset({ assetTag: assetTag.trim(), name: name.trim(), categoryId, serial: serial.trim() || undefined }),
    onSuccess: () => { setTag(''); setName(''); setSerial(''); onCreated(); },
  });
  return (
    <form className="rounded border border-gray-900 p-4 flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); if (assetTag.trim() && name.trim() && categoryId) create.mutate(); }}>
      <label className="text-sm text-gray-300 space-y-1"><span>Tag</span><input value={assetTag} onChange={(e) => setTag(e.target.value)} aria-label="Asset tag" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" /></label>
      <label className="text-sm text-gray-300 space-y-1"><span>Name</span><input value={name} onChange={(e) => setName(e.target.value)} aria-label="Asset name" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" /></label>
      <label className="text-sm text-gray-300 space-y-1"><span>Category</span>
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-label="Asset category" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm">
          <option value="">Select…</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </label>
      <label className="text-sm text-gray-300 space-y-1"><span>Serial <span className="text-gray-500">(optional)</span></span><input value={serial} onChange={(e) => setSerial(e.target.value)} aria-label="Serial" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" /></label>
      <button type="submit" disabled={!assetTag.trim() || !name.trim() || !categoryId || create.isPending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50">Add asset</button>
      <ApiErrorNotice error={create.error} fallback="Could not create asset." className="w-full" />
    </form>
  );
}

function AssetRow({ a, categoryLabel, onChanged }: { a: Asset; categoryLabel: string; onChanged: () => void }): JSX.Element {
  const setStatus = useMutation({ mutationFn: (status: 'available' | 'retired' | 'lost') => updateAsset(a.id, { status }), onSuccess: onChanged });
  // Recover a held asset back into the pool — the same custody return as the
  // Requisitions worklist, reachable where you see the asset (keyed by its open
  // assignment). Retire/Mark-lost are gone while assigned: the server blocks
  // marking a still-held asset (409), so you return it first.
  const recover = useMutation({ mutationFn: () => returnAsset(a.openAssignmentId!), onSuccess: onChanged });
  const inPool = a.status === 'available';
  return (
    <tr className="border-t border-gray-900">
      <td className="py-2 pr-4 font-mono text-gray-300">{a.assetTag}</td>
      <td className="py-2 pr-4 text-gray-200">{a.name}</td>
      <td className="py-2 pr-4 text-gray-400">{categoryLabel}</td>
      <td className="py-2 pr-4 text-gray-500">{a.serial ?? '—'}</td>
      <td className="py-2 pr-4"><AssetStatusBadge status={a.status} /></td>
      <td className="py-2 space-x-2">
        {a.status === 'assigned' && a.openAssignmentId ? <button type="button" onClick={() => recover.mutate()} disabled={recover.isPending} className="text-xs text-green-300 underline">Return</button> : null}
        {inPool ? <button type="button" onClick={() => setStatus.mutate('retired')} className="text-xs text-yellow-300 underline">Retire</button> : null}
        {inPool ? <button type="button" onClick={() => setStatus.mutate('lost')} className="text-xs text-red-400 underline">Mark lost</button> : null}
        {a.status === 'retired' || a.status === 'lost' ? <button type="button" onClick={() => setStatus.mutate('available')} className="text-xs text-green-300 underline">Restore</button> : null}
        <ApiErrorNotice error={setStatus.error ?? recover.error} fallback="Action failed." compact className="mt-1" />
      </td>
    </tr>
  );
}
